/**
 * Para onde o toque numa notificação leva — o mesmo para o item do sino (NavBar) e para o push
 * tocado (hooks/usePushNativo.js). O raciocínio está em prompts/notificacao-leva-ao-destino.md.
 *
 * O backend grava o ASSUNTO da notificação (`notificacoes_destino`, V34), nunca a rota: a tela é
 * decisão daqui. Chega por dois caminhos, com nomes e tipos diferentes:
 *
 * - sino (`/notificacao/listar`): `tipoDestino`, `idAgenda`, `idProfissionalDestino` e
 *   `cidadeDestino` (números);
 * - push (`data` do FCM, só strings): `destino`, `agendaId`, `profissionalId` e `cidade`.
 *
 * Tipo desconhecido devolve `null`, e não um palpite: um tipo novo do backend chegando a um app
 * antigo tem de cair no "sem destino" (o push abre o painel; o item do sino fica sem clique).
 */

const ROTAS = {
	// Sem id não há agendamento para abrir; melhor sem destino que uma Agenda abrindo nada.
	AGENDA: ({ idAgenda }) => (idAgenda ? `/agenda?agendamento=${encodeURIComponent(idAgenda)}` : null),
	VERIFICACAO: () => '/verificacao/documento',
	ASSINATURA: () => '/usuario/editar?secao=assinatura',
	// "Já temos profissional na sua cidade" (V40): a busca abre nessa cidade, listando todo mundo, com
	// o painel do profissional aberto — de onde a pessoa já pede o serviço. Sem os dois, sem destino.
	BUSCA: ({ idProfissional, cidade }) => (idProfissional && cidade
		? `/profissional?cidade=${encodeURIComponent(cidade)}&profissional=${encodeURIComponent(idProfissional)}`
		: null),
};

/**
 * `tipo` é o nome do campo desde que a API passou a responder a notificação como DTO (F4);
 * `tipoDestino` é o nome antigo, que o backend ainda repete enquanto houver app desatualizado. Lê os
 * dois, com o novo na frente.
 *
 * @param {{ tipo?: string|null, tipoDestino?: string|null, idAgenda?: number|string|null,
 *           idProfissionalDestino?: number|string|null, cidadeDestino?: number|string|null }} destino
 * @returns {string|null} a rota, ou null quando a notificação não leva a lugar nenhum
 */
export function rotaDaNotificacao({ tipo, tipoDestino, idAgenda, idProfissionalDestino, cidadeDestino } = {}) {
	const tipoDaRota = tipo ?? tipoDestino;
	const rota = tipoDaRota && Object.prototype.hasOwnProperty.call(ROTAS, tipoDaRota) ? ROTAS[tipoDaRota] : null;
	return rota ? rota({ idAgenda, idProfissional: idProfissionalDestino, cidade: cidadeDestino }) : null;
}

/** O mesmo, a partir do `data` do push (`destino` / `agendaId` / `profissionalId` / `cidade`). */
export function rotaDoPush(dados = {}) {
	return rotaDaNotificacao({
		tipo: dados.destino,
		idAgenda: dados.agendaId,
		idProfissionalDestino: dados.profissionalId,
		cidadeDestino: dados.cidade,
	});
}
